import type { EffectInstance, EffectType, WamParameterInfo } from '@/domain/types';
import type { WamNode as IWamNode } from '@webaudiomodules/api';
import { loadWam, type LoadedWam } from './wamHost';
import type { TransportTell } from '../wamTransport';
import { dbToGain } from '@/util/assert';
import { EFFECT_DESCRIPTORS, defaultParams } from './descriptors';
import { SATURATOR_PROCESSOR, saturatorModule } from './saturatorWorklet';

/**
 * Effects are built from native Web Audio nodes wherever a native node does the
 * job correctly. That is not laziness: `DynamicsCompressorNode`, `BiquadFilter`
 * and `ConvolverNode` run in the audio thread in optimised native code, and
 * reimplementing them in an AudioWorklet would cost CPU without improving the
 * sound. AudioWorklet is reserved for processing the platform genuinely does
 * not provide — metering and recording capture — where it is the only correct
 * tool.
 *
 * Every effect exposes the same shape: an input node, an output node, a
 * parameter setter that ramps rather than steps (so automation and knob drags
 * never click), and a disposer that releases every node it created.
 */

/**
 * What the automation writer needs of a target: the four members of
 * `AudioParam` it calls. An `AudioParam` is one; so is a hosted plugin's
 * parameter, which has no `AudioParam` and takes scheduled events instead —
 * see `WamParamProxy`.
 */
export type AutomationParamLike = Pick<AudioParam, 'value' | 'setValueAtTime' | 'linearRampToValueAtTime' | 'cancelScheduledValues'>;

export interface AutomatableParam {
  param: AutomationParamLike;
  /** Maps the parameter's own units onto this AudioParam's units. */
  map: (value: number) => number;
  /**
   * True when `map` is not a straight line. An AudioParam ramp interpolates
   * linearly in the param's own units, so a non-linear mapping has to be
   * sampled along the way or the shape the user drew is not the shape they
   * hear.
   */
  nonlinear?: boolean;
}

const identity = (value: number): number => value;

export interface EffectNode {
  readonly type: EffectType;
  readonly input: AudioNode;
  readonly output: AudioNode;
  setParam(paramId: string, value: number, atTime?: number): void;
  setEnabled(enabled: boolean): void;
  /**
   * For a reverb: play this recording of a room instead of the synthesised
   * tail, or null to go back to it. Absent on every other effect.
   */
  setImpulse?(buffer: AudioBuffer | null): void;
  /**
   * The `AudioParam`s a parameter drives, with the mapping from the parameter's
   * own units onto each one.
   *
   * Automation needs the params themselves, not a setter: scheduled ramps have
   * to be written ahead of time onto the audio thread's timeline. Most
   * parameters map one-to-one, but some do not — a dry/wet `mix` drives two
   * gains through an equal-power law, and a makeup control in decibels drives a
   * linear gain. Returning the list with its mappings keeps those cases
   * sample-accurate instead of demoting them to a coarse, main-thread update.
   *
   * An empty list means the parameter cannot be ramped (a filter's type, a
   * reverb's impulse length). Those descriptors are marked
   * `automatable: false`, so no lane is ever offered for them.
   */
  paramTargets(paramId: string): AutomatableParam[];
  /**
   * Settles once every asynchronous part of the effect is wired — a worklet
   * module loaded and its node in the chain. Absent on an effect built
   * entirely from platform nodes, which is ready the moment it exists. Until
   * it settles the effect passes audio through unprocessed, and a parameter
   * that lives in the worklet has no `AudioParam` to offer automation.
   */
  ready?: Promise<void>;
  /**
   * For a hosted plugin: what it said about itself once it loaded. Settles
   * with `ready`; the engine hands it to the store so the project remembers.
   */
  wamInfo?: Promise<{ name: string; parameters: WamParameterInfo[] }>;
  /** For a hosted plugin: its own interface, made on demand, and taken down. */
  createGui?(): Promise<Element>;
  destroyGui?(gui: Element): void;
  /** For a hosted plugin: its whole state as JSON, or null while it is not loaded. */
  captureState?(): Promise<unknown>;
  /** For a hosted plugin: the transport, as `wam-transport` events on the plugin's node. */
  tellTransport?(tells: readonly TransportTell[]): void;
  /**
   * How much later than its input this effect's output arrives, in seconds.
   * Absent on an effect built from platform nodes, which answers at once;
   * a hosted plugin reports what the plugin says, and nothing until it loads.
   */
  latencySec?(): number;
  dispose(): void;
}

const RAMP = 0.02;

function ramp(param: AudioParam, value: number, ctx: BaseAudioContext, atTime?: number): void {
  const t = atTime ?? ctx.currentTime;
  const safe = Number.isFinite(value) ? value : 0;
  try {
    param.setTargetAtTime(safe, t, RAMP);
  } catch {
    param.value = safe;
  }
}

/**
 * Generates a reverb impulse: exponentially decaying noise, low-passed over
 * time so high frequencies die away first, which is what real rooms do.
 */
function buildImpulse(ctx: BaseAudioContext, seconds: number, damping: number): AudioBuffer {
  const rate = ctx.sampleRate;
  const length = Math.max(1, Math.floor(seconds * rate));
  const impulse = ctx.createBuffer(2, length, rate);
  // One-pole coefficient for the damping cutoff.
  const coeff = Math.exp((-2 * Math.PI * damping) / rate);
  for (let channel = 0; channel < 2; channel++) {
    const data = impulse.getChannelData(channel);
    let previous = 0;
    for (let i = 0; i < length; i++) {
      const white = Math.random() * 2 - 1;
      previous = white * (1 - coeff) + previous * coeff;
      const decay = Math.pow(1 - i / length, 2.5);
      data[i] = previous * decay;
    }
  }
  return impulse;
}

interface Wet {
  input: GainNode;
  output: GainNode;
  dry: GainNode;
  wet: GainNode;
}

/** Standard dry/wet pair used by the time-based effects. */
function makeWetDry(ctx: BaseAudioContext): Wet {
  const input = ctx.createGain();
  const output = ctx.createGain();
  const dry = ctx.createGain();
  const wet = ctx.createGain();
  input.connect(dry).connect(output);
  wet.connect(output);
  return { input, output, dry, wet };
}

/**
 * The dry/wet pair as two automatable params. Automating `mix` therefore ramps
 * both gains along the same equal-power law the knob uses, rather than being
 * demoted to a stepped, main-thread update.
 */
function equalPowerMix(w: Wet): AutomatableParam[] {
  return [
    { param: w.dry.gain, map: (m) => Math.cos((Math.min(1, Math.max(0, m)) * Math.PI) / 2), nonlinear: true },
    { param: w.wet.gain, map: (m) => Math.sin((Math.min(1, Math.max(0, m)) * Math.PI) / 2), nonlinear: true },
  ];
}

function setMix(w: Wet, mix: number, ctx: BaseAudioContext, atTime?: number): void {
  // Equal-power crossfade, so a 50% mix is not 6dB quieter than either extreme.
  const m = Math.min(1, Math.max(0, mix));
  ramp(w.dry.gain, Math.cos((m * Math.PI) / 2), ctx, atTime);
  ramp(w.wet.gain, Math.sin((m * Math.PI) / 2), ctx, atTime);
}

export function createEffectNode(ctx: BaseAudioContext, instance: EffectInstance): EffectNode {
  const params = { ...defaultParams(instance.type), ...instance.params };
  const node = build(ctx, instance.type, instance);
  for (const [id, value] of Object.entries(params)) node.setParam(id, value, ctx.currentTime);
  node.setEnabled(instance.enabled);
  return node;
}

function build(ctx: BaseAudioContext, type: EffectType, instance: EffectInstance): EffectNode {
  switch (type) {
    case 'eq3':
      return buildEq3(ctx);
    case 'compressor':
      return buildCompressor(ctx);
    case 'reverb':
      return buildReverb(ctx);
    case 'delay':
      return buildDelay(ctx);
    case 'filter':
      return buildFilter(ctx);
    case 'limiter':
      return buildLimiter(ctx);
    case 'chorus':
      return buildChorus(ctx);
    case 'saturator':
      return buildSaturator(ctx);
    case 'wam':
      return buildWam(ctx, instance.wamUrl ?? '', instance.wamState);
    default:
      return buildPassthrough(ctx, type);
  }
}

/** Bypass is implemented as a parallel path so enabling it never re-patches the graph. */
function withBypass(
  ctx: BaseAudioContext,
  type: EffectType,
  wetChainInput: AudioNode,
  wetChainOutput: AudioNode,
  setParam: (id: string, value: number, atTime?: number) => void,
  disposeNodes: () => void,
  params: Record<string, AutomatableParam[]> = {},
): EffectNode {
  const input = ctx.createGain();
  const output = ctx.createGain();
  const processed = ctx.createGain();
  const bypassed = ctx.createGain();

  input.connect(wetChainInput);
  wetChainOutput.connect(processed).connect(output);
  input.connect(bypassed).connect(output);
  bypassed.gain.value = 0;

  return {
    type,
    input,
    output,
    setParam,
    paramTargets: (paramId) => params[paramId] ?? [],
    setEnabled(enabled) {
      ramp(processed.gain, enabled ? 1 : 0, ctx);
      ramp(bypassed.gain, enabled ? 0 : 1, ctx);
    },
    dispose() {
      input.disconnect();
      output.disconnect();
      processed.disconnect();
      bypassed.disconnect();
      disposeNodes();
    },
  };
}

function buildPassthrough(ctx: BaseAudioContext, type: EffectType): EffectNode {
  const gain = ctx.createGain();
  return {
    type,
    input: gain,
    output: gain,
    setParam: () => undefined,
    paramTargets: () => [],
    setEnabled: () => undefined,
    dispose: () => gain.disconnect(),
  };
}

function buildEq3(ctx: BaseAudioContext): EffectNode {
  const low = ctx.createBiquadFilter();
  low.type = 'lowshelf';
  const mid = ctx.createBiquadFilter();
  mid.type = 'peaking';
  const high = ctx.createBiquadFilter();
  high.type = 'highshelf';
  low.connect(mid).connect(high);

  return withBypass(
    ctx,
    'eq3',
    low,
    high,
    (id, value, atTime) => {
      switch (id) {
        case 'lowGain':
          return ramp(low.gain, value, ctx, atTime);
        case 'lowFreq':
          return ramp(low.frequency, value, ctx, atTime);
        case 'midGain':
          return ramp(mid.gain, value, ctx, atTime);
        case 'midFreq':
          return ramp(mid.frequency, value, ctx, atTime);
        case 'midQ':
          return ramp(mid.Q, value, ctx, atTime);
        case 'highGain':
          return ramp(high.gain, value, ctx, atTime);
        case 'highFreq':
          return ramp(high.frequency, value, ctx, atTime);
      }
    },
    () => {
      low.disconnect();
      mid.disconnect();
      high.disconnect();
    },
    {
      lowGain: [{ param: low.gain, map: identity }],
      lowFreq: [{ param: low.frequency, map: identity }],
      midGain: [{ param: mid.gain, map: identity }],
      midFreq: [{ param: mid.frequency, map: identity }],
      midQ: [{ param: mid.Q, map: identity }],
      highGain: [{ param: high.gain, map: identity }],
      highFreq: [{ param: high.frequency, map: identity }],
    },
  );
}

function buildCompressor(ctx: BaseAudioContext): EffectNode {
  const comp = ctx.createDynamicsCompressor();
  const makeup = ctx.createGain();
  comp.connect(makeup);

  return withBypass(
    ctx,
    'compressor',
    comp,
    makeup,
    (id, value, atTime) => {
      switch (id) {
        case 'threshold':
          return ramp(comp.threshold, value, ctx, atTime);
        case 'ratio':
          return ramp(comp.ratio, value, ctx, atTime);
        case 'attack':
          return ramp(comp.attack, value, ctx, atTime);
        case 'release':
          return ramp(comp.release, value, ctx, atTime);
        case 'knee':
          return ramp(comp.knee, value, ctx, atTime);
        case 'makeup':
          return ramp(makeup.gain, dbToGain(value), ctx, atTime);
      }
    },
    () => {
      comp.disconnect();
      makeup.disconnect();
    },
    {
      threshold: [{ param: comp.threshold, map: identity }],
      ratio: [{ param: comp.ratio, map: identity }],
      attack: [{ param: comp.attack, map: identity }],
      release: [{ param: comp.release, map: identity }],
      knee: [{ param: comp.knee, map: identity }],
      // Makeup is expressed in decibels; the node is a linear gain.
      makeup: [{ param: makeup.gain, map: dbToGain, nonlinear: true }],
    },
  );
}

function buildReverb(ctx: BaseAudioContext): EffectNode {
  const w = makeWetDry(ctx);
  const predelay = ctx.createDelay(1);
  const convolver = ctx.createConvolver();
  w.input.connect(predelay).connect(convolver).connect(w.wet);

  let size = 2;
  let damping = 6000;
  let impulseToken = 0;
  // A recorded room, when one is set. It takes the place of the synthesised
  // tail entirely: size and damping describe the tail, not the room.
  let room: AudioBuffer | null = null;

  const rebuildImpulse = (): void => {
    // Impulse generation is O(seconds * sampleRate); debounce it so dragging the
    // size control does not rebuild a 400k-sample buffer on every mouse move.
    const token = ++impulseToken;
    const run = (): void => {
      if (token !== impulseToken) return;
      convolver.buffer = room ?? buildImpulse(ctx, size, damping);
    };
    if (typeof queueMicrotask === 'function') setTimeout(run, 60);
    else run();
  };
  rebuildImpulse();

  const node = withBypass(
    ctx,
    'reverb',
    w.input,
    w.output,
    (id, value, atTime) => {
      switch (id) {
        case 'size':
          size = value;
          return rebuildImpulse();
        case 'damping':
          damping = value;
          return rebuildImpulse();
        case 'predelay':
          return ramp(predelay.delayTime, value, ctx, atTime);
        case 'mix':
          return setMix(w, value, ctx, atTime);
      }
    },
    () => {
      w.input.disconnect();
      w.output.disconnect();
      w.dry.disconnect();
      w.wet.disconnect();
      predelay.disconnect();
      convolver.disconnect();
    },
    {
      predelay: [{ param: predelay.delayTime, map: identity }],
      mix: equalPowerMix(w),
    },
  );
  return {
    ...node,
    setImpulse(buffer) {
      if (buffer === room) return;
      room = buffer;
      // Straight away, not debounced: a room arriving is one event, not a drag.
      impulseToken += 1;
      convolver.buffer = room ?? buildImpulse(ctx, size, damping);
    },
  };
}

/**
 * Chorus: the input played back through two short delays whose lengths an
 * oscillator sweeps, one for each side, the two sweeps a quarter-cycle apart.
 *
 * A moving delay is a pitch shift — the read point running away from the
 * write point lowers the pitch, running towards it raises it — so each copy
 * wavers around the original by a few cents, and two copies wavering
 * differently on the two sides is what widens the sound. The centre of the
 * sweep sits at a few milliseconds and the depth never reaches zero, so the
 * delay is never asked to go negative. Nothing here is a worklet: three
 * platform nodes and two gains do the whole thing.
 */
function buildChorus(ctx: BaseAudioContext): EffectNode {
  const w = makeWetDry(ctx);
  const CENTRE_SEC = 0.012;

  const left = ctx.createDelay(0.1);
  const right = ctx.createDelay(0.1);
  left.delayTime.value = CENTRE_SEC;
  right.delayTime.value = CENTRE_SEC;

  // Two sweeps a quarter-cycle apart: a sine for the left and a cosine for
  // the right, as periodic waves, so when the left copy is at its highest
  // the right is passing through the middle. Two waves rather than one
  // oscillator started later, because an oscillator has no phase control and
  // where its phase sits at a late start is an implementation's choice — the
  // test renderer counts phase from the context's clock, not from the start.
  const lfoLeft = ctx.createOscillator();
  const lfoRight = ctx.createOscillator();
  lfoLeft.setPeriodicWave(ctx.createPeriodicWave(new Float32Array([0, 0]), new Float32Array([0, 1])));
  lfoRight.setPeriodicWave(ctx.createPeriodicWave(new Float32Array([0, 1]), new Float32Array([0, 0])));
  const depthLeft = ctx.createGain();
  const depthRight = ctx.createGain();
  depthLeft.gain.value = 0;
  depthRight.gain.value = 0;
  lfoLeft.connect(depthLeft).connect(left.delayTime);
  lfoRight.connect(depthRight).connect(right.delayTime);

  // Spread: at 1 the left copy goes left and the right copy right; at 0 each
  // side also gets the other copy at full, and a normaliser keeps the total
  // the same, so the effect is mono. The merger sums each copy to one channel
  // on the way in, which is what a chorus does with a stereo source anyway.
  const merger = ctx.createChannelMerger(2);
  const leftCross = ctx.createGain();
  const rightCross = ctx.createGain();
  const norm = ctx.createGain();
  leftCross.gain.value = 0;
  rightCross.gain.value = 0;
  norm.gain.value = 1;
  left.connect(merger, 0, 0);
  right.connect(merger, 0, 1);
  left.connect(leftCross).connect(merger, 0, 1);
  right.connect(rightCross).connect(merger, 0, 0);
  merger.connect(norm).connect(w.wet);
  w.input.connect(left);
  w.input.connect(right);

  let rate = 0.8;
  let depthSec = 0.003;
  let started = false;
  const start = (): void => {
    if (started) return;
    started = true;
    lfoLeft.start(ctx.currentTime);
    lfoRight.start(ctx.currentTime);
  };
  const applyDepth = (atTime?: number): void => {
    // The sweep may never take the delay below zero; the centre is twelve
    // milliseconds and the depth ten at most, so it never does.
    ramp(depthLeft.gain, Math.min(depthSec, CENTRE_SEC * 0.9), ctx, atTime);
    ramp(depthRight.gain, Math.min(depthSec, CENTRE_SEC * 0.9), ctx, atTime);
  };

  const node = withBypass(
    ctx,
    'chorus',
    w.input,
    w.output,
    (id, value, atTime) => {
      switch (id) {
        case 'rate':
          rate = Math.max(0.05, value);
          ramp(lfoLeft.frequency, rate, ctx, atTime);
          ramp(lfoRight.frequency, rate, ctx, atTime);
          start();
          return;
        case 'depth':
          depthSec = Math.max(0, value) / 1000;
          applyDepth(atTime);
          start();
          return;
        case 'spread': {
          const cross = 1 - Math.min(1, Math.max(0, value));
          ramp(leftCross.gain, cross, ctx, atTime);
          ramp(rightCross.gain, cross, ctx, atTime);
          ramp(norm.gain, 1 / (1 + cross), ctx, atTime);
          return;
        }
        case 'mix':
          return setMix(w, value, ctx, atTime);
      }
    },
    () => {
      try {
        lfoLeft.stop();
        lfoRight.stop();
      } catch {
        /* never started */
      }
      for (const n of [w.input, w.output, w.dry, w.wet, left, right, lfoLeft, lfoRight, depthLeft, depthRight, merger, leftCross, rightCross, norm]) {
        n.disconnect();
      }
    },
    {
      rate: [
        { param: lfoLeft.frequency, map: (v) => Math.max(0.05, v) },
        { param: lfoRight.frequency, map: (v) => Math.max(0.05, v) },
      ],
      depth: [
        { param: depthLeft.gain, map: (v) => Math.min(Math.max(0, v) / 1000, CENTRE_SEC * 0.9) },
        { param: depthRight.gain, map: (v) => Math.min(Math.max(0, v) / 1000, CENTRE_SEC * 0.9) },
      ],
      mix: equalPowerMix(w),
    },
  );
  return node;
}

/**
 * A hosted plugin's parameter, wearing enough of `AudioParam` for the
 * automation writer.
 *
 * The plugin takes its automation as scheduled events, one value at one
 * time, and smooths between them over a few milliseconds of its own. A step
 * is one event. A ramp is sampled: the writer already subdivides a ramp for
 * a target it is told is non-linear, and this proxy subdivides again to keep
 * every event within twenty milliseconds of the last, so a slow ramp is
 * heard as a ramp. Cancelling clears every event the node holds, which is
 * what the writer means by it: it rewrites all its lanes together.
 */
export class WamParamProxy implements AutomationParamLike {
  private lastValue: number;
  private lastTime = 0;
  private static readonly MAX_STEP_SEC = 0.02;

  constructor(
    private readonly node: IWamNode,
    private readonly id: string,
    initial: number,
  ) {
    this.lastValue = initial;
  }

  get value(): number {
    return this.lastValue;
  }

  set value(v: number) {
    this.lastValue = v;
    void this.node.setParameterValues({ [this.id]: { id: this.id, value: v, normalized: false } });
  }

  setValueAtTime(value: number, time: number): AudioParam {
    this.node.scheduleEvents({ type: 'wam-automation', time, data: { id: this.id, value, normalized: false } });
    this.lastValue = value;
    this.lastTime = time;
    return this as unknown as AudioParam;
  }

  linearRampToValueAtTime(value: number, time: number): AudioParam {
    const from = this.lastValue;
    const fromTime = this.lastTime;
    const span = time - fromTime;
    const steps = span > 0 ? Math.max(1, Math.ceil(span / WamParamProxy.MAX_STEP_SEC)) : 1;
    for (let i = 1; i <= steps; i++) {
      const t = fromTime + (span * i) / steps;
      const v = from + ((value - from) * i) / steps;
      this.node.scheduleEvents({ type: 'wam-automation', time: t, data: { id: this.id, value: v, normalized: false } });
    }
    this.lastValue = value;
    this.lastTime = time;
    return this as unknown as AudioParam;
  }

  cancelScheduledValues(time: number): AudioParam {
    void this.node.clearEvents();
    this.lastTime = time;
    return this as unknown as AudioParam;
  }
}

/**
 * A hosted Web Audio Module as an insert.
 *
 * The plugin loads asynchronously — its host environment, its module, its
 * instance — so the wet path begins as a wire and the plugin's node is
 * spliced in when it lands, exactly as the saturator's worklet is. Its
 * parameters have no `AudioParam`; each is offered to automation through a
 * proxy that turns the writer's calls into the plugin's scheduled events.
 * A plugin that fails to load leaves an insert that passes audio through and
 * says why in the console, rather than a chain that throws.
 */
function buildWam(ctx: BaseAudioContext, url: string, savedState: unknown): EffectNode {
  const w = makeWetDry(ctx);
  const through = ctx.createGain();
  w.input.connect(through).connect(w.wet);

  let loaded: LoadedWam | null = null;
  let disposed = false;
  const wanted = new Map<string, number>();
  const proxies = new Map<string, WamParamProxy>();

  const wamInfo = loadWam(ctx, url).then((result) => {
    if (disposed) {
      result.node.destroy();
      throw new Error('disposed');
    }
    loaded = result;
    w.input.connect(result.node).connect(w.wet);
    w.input.disconnect(through);
    through.disconnect();
    return restoreWam(result, savedState, wanted).then(() => ({ name: result.name, parameters: result.parameters }));
  });
  const ready = wamInfo.then(
    () => undefined,
    (error: unknown) => {
      if (disposed) return;
      console.warn(`The plugin at ${url} could not be loaded; its insert is passing audio through.`, error);
    },
  );

  return {
    ...withBypass(
      ctx,
      'wam',
      w.input,
      w.output,
      (id, value) => {
        if (id === 'mix') return setMix(w, value, ctx);
        wanted.set(id, value);
        if (loaded) void loaded.node.setParameterValues({ [id]: { id, value, normalized: false } });
      },
      () => {
        disposed = true;
        for (const n of [w.input, w.output, w.dry, w.wet, through]) n.disconnect();
        if (loaded) {
          loaded.node.disconnect();
          loaded.node.destroy();
          loaded = null;
        }
      },
      {},
    ),
    ready,
    wamInfo,
    paramTargets(paramId) {
      if (paramId === 'mix') return equalPowerMix(w);
      if (!loaded || !loaded.parameters.some((p) => p.id === paramId)) return [];
      let proxy = proxies.get(paramId);
      if (!proxy) {
        proxy = new WamParamProxy(loaded.node, paramId, wanted.get(paramId) ?? 0);
        proxies.set(paramId, proxy);
      }
      return [{ param: proxy, map: identity, nonlinear: true }];
    },
    async createGui() {
      await wamInfo;
      if (!loaded) throw new Error('The plugin is not loaded.');
      return loaded.instance.createGui();
    },
    destroyGui(gui) {
      loaded?.instance.destroyGui(gui);
    },
    async captureState() {
      return loaded ? loaded.node.getState() : null;
    },
    tellTransport(tells) {
      if (loaded) tellWam(loaded, tells);
    },
    latencySec() {
      return loaded?.latencySec ?? 0;
    },
  };
}

/** Hands a loaded plugin the transport, one event per tell. */
export function tellWam(loaded: LoadedWam, tells: readonly TransportTell[]): void {
  for (const tell of tells) {
    const { time, ...data } = tell;
    loaded.node.scheduleEvents({ type: 'wam-transport', time, data });
  }
}

/**
 * Hands a freshly loaded plugin what the project kept of it: its saved state
 * first, then the parameter values the project holds, so a value set through
 * a command since the state was captured wins over the state's copy of it.
 */
export async function restoreWam(
  loaded: LoadedWam,
  savedState: unknown,
  wanted: ReadonlyMap<string, number>,
): Promise<void> {
  if (savedState !== undefined && savedState !== null) {
    try {
      await loaded.node.setState(savedState);
    } catch (error) {
      console.warn('The plugin refused its saved state; it starts fresh.', error);
    }
  }
  const values: Record<string, { id: string; value: number; normalized: boolean }> = {};
  for (const [id, value] of wanted) values[id] = { id, value, normalized: false };
  if (Object.keys(values).length > 0) await loaded.node.setParameterValues(values);
}

/**
 * Saturator: a soft clip in an AudioWorklet, then a tone filter.
 *
 * The worklet module loads asynchronously, and an `AudioWorkletNode` cannot
 * be made before its processor is registered, so the wet path begins as a
 * plain wire and the worklet is spliced in when the module has loaded — a few
 * milliseconds live, and before the first sample of an offline render, which
 * waits on `ready`. Until then `drive` has no parameter to offer automation;
 * the render waits for that too, and the live engine reschedules its lanes
 * once the node is in.
 */
function buildSaturator(ctx: BaseAudioContext): EffectNode {
  const w = makeWetDry(ctx);
  const through = ctx.createGain();
  const tone = ctx.createBiquadFilter();
  tone.type = 'lowpass';
  w.input.connect(through).connect(tone).connect(w.wet);

  let worklet: AudioWorkletNode | null = null;
  let driveWanted = 4;
  let disposed = false;

  const ready = (ctx.audioWorklet ? saturatorModule.ensure(ctx) : Promise.reject(new Error('This context has no AudioWorklet.')))
    .then(() => {
      if (disposed) return;
      const node = new AudioWorkletNode(ctx, SATURATOR_PROCESSOR, { numberOfInputs: 1, numberOfOutputs: 1 });
      const drive = node.parameters.get('drive');
      if (drive) drive.value = driveWanted;
      // The wire out, the worklet in, in one step: the input feeds the
      // worklet before the wire is dropped, so nothing is missed between.
      w.input.connect(node).connect(tone);
      w.input.disconnect(through);
      through.disconnect();
      worklet = node;
    })
    .catch((error: unknown) => {
      // No worklets here — an old browser, or a context that refuses them.
      // The saturator is then a tone control, which is honest: the chain
      // still passes audio and nothing throws. Said once, so a missing
      // worklet is not a silent change to the sound.
      console.warn('The saturator could not load its worklet; it is passing audio through unprocessed.', error);
    });

  const driveParam = (): AudioParam | null => worklet?.parameters.get('drive') ?? null;

  return {
    ...withBypass(
      ctx,
      'saturator',
      w.input,
      w.output,
      (id, value, atTime) => {
        switch (id) {
          case 'drive': {
            driveWanted = Math.max(1, Math.min(20, value));
            const param = driveParam();
            if (param) ramp(param, driveWanted, ctx, atTime);
            return;
          }
          case 'tone':
            return ramp(tone.frequency, value, ctx, atTime);
          case 'mix':
            return setMix(w, value, ctx, atTime);
        }
      },
      () => {
        disposed = true;
        for (const n of [w.input, w.output, w.dry, w.wet, through, tone]) n.disconnect();
        worklet?.disconnect();
        worklet = null;
      },
      {
        tone: [{ param: tone.frequency, map: identity }],
        mix: equalPowerMix(w),
      },
    ),
    ready,
    paramTargets(paramId) {
      if (paramId === 'drive') {
        const param = driveParam();
        return param ? [{ param, map: (v) => Math.max(1, Math.min(20, v)) }] : [];
      }
      if (paramId === 'tone') return [{ param: tone.frequency, map: identity }];
      if (paramId === 'mix') return equalPowerMix(w);
      return [];
    },
  };
}

function buildDelay(ctx: BaseAudioContext): EffectNode {
  const w = makeWetDry(ctx);
  const delay = ctx.createDelay(2);
  const feedback = ctx.createGain();
  const tone = ctx.createBiquadFilter();
  tone.type = 'lowpass';

  w.input.connect(delay);
  delay.connect(tone).connect(feedback).connect(delay);
  delay.connect(w.wet);

  return withBypass(
    ctx,
    'delay',
    w.input,
    w.output,
    (id, value, atTime) => {
      switch (id) {
        case 'time':
          return ramp(delay.delayTime, value, ctx, atTime);
        case 'feedback':
          // Hard-capped below 1.0: a feedback loop at unity gain is a runaway
          // that ends in a very loud noise and a damaged pair of ears.
          return ramp(feedback.gain, Math.min(0.95, Math.max(0, value)), ctx, atTime);
        case 'tone':
          return ramp(tone.frequency, value, ctx, atTime);
        case 'mix':
          return setMix(w, value, ctx, atTime);
      }
    },
    () => {
      w.input.disconnect();
      w.output.disconnect();
      w.dry.disconnect();
      w.wet.disconnect();
      delay.disconnect();
      feedback.disconnect();
      tone.disconnect();
    },
    {
      time: [{ param: delay.delayTime, map: identity }],
      feedback: [{ param: feedback.gain, map: (v) => Math.min(0.95, Math.max(0, v)) }],
      tone: [{ param: tone.frequency, map: identity }],
      mix: equalPowerMix(w),
    },
  );
}

function buildFilter(ctx: BaseAudioContext): EffectNode {
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  const types: BiquadFilterType[] = ['lowpass', 'highpass', 'bandpass', 'notch'];

  return withBypass(
    ctx,
    'filter',
    filter,
    filter,
    (id, value, atTime) => {
      switch (id) {
        case 'type':
          filter.type = types[Math.min(types.length - 1, Math.max(0, Math.round(value)))];
          return;
        case 'frequency':
          return ramp(filter.frequency, value, ctx, atTime);
        case 'resonance':
          return ramp(filter.Q, value, ctx, atTime);
      }
    },
    () => filter.disconnect(),
    { frequency: [{ param: filter.frequency, map: identity }], resonance: [{ param: filter.Q, map: identity }] },
  );
}

function buildLimiter(ctx: BaseAudioContext): EffectNode {
  const comp = ctx.createDynamicsCompressor();
  comp.ratio.value = 20;
  comp.knee.value = 0;
  comp.attack.value = 0.001;
  const trim = ctx.createGain();
  comp.connect(trim);

  return withBypass(
    ctx,
    'limiter',
    comp,
    trim,
    (id, value, atTime) => {
      switch (id) {
        case 'ceiling':
          ramp(comp.threshold, value, ctx, atTime);
          // Compensate so the ceiling behaves as an output ceiling rather than
          // a threshold: below it, level is unchanged.
          return ramp(trim.gain, dbToGain(-value * 0.15), ctx, atTime);
        case 'release':
          return ramp(comp.release, value, ctx, atTime);
      }
    },
    () => {
      comp.disconnect();
      trim.disconnect();
    },
    { release: [{ param: comp.release, map: identity }] },
  );
}

export function effectLabel(type: EffectType): string {
  return EFFECT_DESCRIPTORS[type].label;
}
